import { InfiniteData, useMutation, useQueryClient } from 'react-query';

import { HttpMethod } from '@api/enums';
import { createHttpClient } from '@api/http-client';
import { END_POINTS } from '@constants/api-endpoints';
import { QUERY_KEYS } from '@constants/query-keys';
import { AlertType } from '@enums/alert';
import { showAlert } from '@lib/alert/alert-manager';

import { Label, LabelModalData, LabelsResponseType } from '../types';

type LabelsUpdatePayload = LabelModalData & { labelId: number };

export const useUpdateLabel = () => {
  const client = createHttpClient();
  const queryClient = useQueryClient();

  // Updates the cached labels list after update
  const updateLabelCache = (updatedLabel: Label) => {
    queryClient.setQueriesData<InfiniteData<LabelsResponseType | undefined>>(
      {
        predicate: query => query.queryKey[0] === QUERY_KEYS.LABELS.LABEL_LIST,
      },
      cachedLabelsData => {
        if (!cachedLabelsData) return { pages: [], pageParams: [] };

        const updatedLabelsPages = cachedLabelsData.pages.map(page => {
          if (!page) return page;

          const updatedLabelItems = page.items.map(item =>
            item.id === updatedLabel.id ? updatedLabel : item,
          );

          return {
            ...page,
            items: updatedLabelItems,
          };
        });

        return {
          ...cachedLabelsData,
          pages: updatedLabelsPages,
        };
      },
    );
  };

  const updateBrandAssetsCache = (updatedLabel: Label) => {
    const brandAssetsQueries = queryClient.getQueriesData<InfiniteData<any>>({
      predicate: query =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === QUERY_KEYS.BRAND_LIBRARY.BRAND_ASSETS_LIST,
    });

    brandAssetsQueries.forEach(([queryKey, cachedBrandAssetsData]) => {
      if (!cachedBrandAssetsData?.pages) return;

      const updatedBrandAssetsPages = cachedBrandAssetsData.pages.map(
        ({ items, ...pageRest }) => {
          const updatedAssetItems = items.map(
            (assetItem: { labels: Label[] }) => {
              const updatedLabels = assetItem.labels.map(label =>
                label.id === updatedLabel.id
                  ? {
                      id: updatedLabel.id,
                      name: updatedLabel.name,
                      parentId: updatedLabel.parentId,
                    }
                  : label,
              );

              return {
                ...assetItem,
                labels: updatedLabels,
              };
            },
          );

          return {
            ...pageRest,
            items: updatedAssetItems,
          };
        },
      );

      queryClient.setQueryData<InfiniteData<any>>(queryKey, {
        ...cachedBrandAssetsData,
        pages: updatedBrandAssetsPages,
      });
    });
  };

  const mutation = useMutation(
    (payload: LabelsUpdatePayload) => {
      const { labelId, name, metadata } = payload;

      return client<Label>({
        method: HttpMethod.PATCH,
        url: END_POINTS.LABELS.UPDATE_LABEL(labelId),
        data: {
          name,
          metadata,
        },
      });
    },
    {
      retry: 0,
      onSuccess: updatedLabel => {
        showAlert({
          type: AlertType.SUCCESS,
          message: 'Label updated successfully',
        });

        updateLabelCache(updatedLabel);
        updateBrandAssetsCache(updatedLabel);
      },
      onError: (error: Error) => {
        showAlert({
          type: AlertType.ERROR,
          message: error.message || 'Failed to update label',
        });
      },
    },
  );

  return mutation;
};
